"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsClient } from "@/hooks/use-is-client";
import { ProductCard } from "@/components/product/product-card";
import type { Product } from "@/types";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calcTimeLeft(endDate: string): TimeLeft | null {
  const diff = new Date(endDate).getTime() - Date.now();
  if (!endDate || diff <= 0) return null;
  const s = Math.floor(diff / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

function CountUnit({
  value,
  label,
  bgColor,
  textColor,
}: {
  value: number;
  label: string;
  bgColor: string;
  textColor: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center text-lg sm:text-xl font-bold tabular-nums"
        style={{
          backgroundColor: `${bgColor}55`,
          border: `1px solid ${bgColor}33`,
          color: textColor,
        }}
      >
        {String(value).padStart(2, "0")}
      </div>
      <span
        className="text-[9px] font-bold uppercase tracking-widest"
        style={{ color: textColor, opacity: 0.55 }}
      >
        {label}
      </span>
    </div>
  );
}

export interface SaleBannerProps {
  bgColor: string;
  glowColor: string;
  subtitle: string;
  subtitleColor: string;
  title: string;
  titleColor: string;
  description: string;
  descColor: string;
  countdownEnabled: boolean;
  countdownEnd: string;
  products?: Product[];
}

export function SaleBanner({
  bgColor,
  glowColor,
  subtitle,
  subtitleColor,
  title,
  titleColor,
  description,
  descColor,
  countdownEnabled,
  countdownEnd,
  products = [],
}: SaleBannerProps) {
  const isClient = useIsClient();
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() =>
    typeof window === "undefined" ? null : calcTimeLeft(countdownEnd),
  );

  useEffect(() => {
    if (!countdownEnabled || !countdownEnd) return;
    const t = setInterval(() => setTimeLeft(calcTimeLeft(countdownEnd)), 1000);
    return () => clearInterval(t);
  }, [countdownEnabled, countdownEnd]);

  const showCountdown = countdownEnabled && countdownEnd;

  // ── Carousel ───────────────────────────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [products.length]);

  const scrollByCards = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  return (
    <div className="relative overflow-hidden rounded-lg" style={{ backgroundColor: bgColor }}>
      {/* Dekoratif ışık */}
      <div
        className="pointer-events-none absolute -top-10 right-24 h-80 w-80 rounded-full opacity-30 blur-[100px]"
        style={{ backgroundColor: glowColor }}
      />

      <div className="relative z-10 flex flex-col gap-7 px-6 py-8 sm:px-10 sm:py-10 md:flex-row md:items-stretch md:gap-10">
        {/* Sol: başlık + sayaç — cam (glass) kart */}
        <div className="glass-shine relative shrink-0 overflow-hidden rounded-2xl border border-white/15 bg-white/5 p-6 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_8px_30px_rgba(0,0,0,0.25)] backdrop-blur-md sm:p-7 md:w-80 md:text-left lg:w-96">
          {subtitle && (
            <p
              className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em]"
              style={{ color: subtitleColor }}
            >
              {subtitle}
            </p>
          )}
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: titleColor }}>
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm" style={{ color: descColor }}>
              {description}
            </p>
          )}

          {showCountdown && (
            <div className="mt-6 flex items-center justify-center gap-2 md:justify-start">
              {isClient && timeLeft ? (
                <>
                  <CountUnit value={timeLeft.days}    label="Gün"  bgColor={glowColor} textColor={titleColor} />
                  <span className="mb-4 text-2xl font-bold" style={{ color: titleColor, opacity: 0.4 }}>:</span>
                  <CountUnit value={timeLeft.hours}   label="Saat" bgColor={glowColor} textColor={titleColor} />
                  <span className="mb-4 text-2xl font-bold" style={{ color: titleColor, opacity: 0.4 }}>:</span>
                  <CountUnit value={timeLeft.minutes} label="Dk."  bgColor={glowColor} textColor={titleColor} />
                  <span className="mb-4 text-2xl font-bold" style={{ color: titleColor, opacity: 0.4 }}>:</span>
                  <CountUnit value={timeLeft.seconds} label="Sn."  bgColor={glowColor} textColor={titleColor} />
                </>
              ) : !isClient ? (
                <div className="h-14 w-[15rem]" aria-hidden="true" />
              ) : (
                <p className="text-sm font-semibold" style={{ color: titleColor, opacity: 0.5 }}>
                  Kampanya sona erdi
                </p>
              )}
            </div>
          )}
        </div>

        {/* Sağ: Ürün carousel'i */}
        {products.length > 0 && (
          <div className="group/carousel relative min-w-0 flex-1">
            <div
              ref={scrollRef}
              className="flex snap-x snap-mandatory items-start gap-3 overflow-x-auto scroll-smooth pb-1 sm:gap-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {products.map((p) => (
                <div key={p.id} className="w-[150px] shrink-0 snap-start sm:w-[180px]">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>

            {/* Oklar — yalnızca kaydırılabilirse görünür (sm+) */}
            {canPrev && (
              <button
                type="button"
                onClick={() => scrollByCards(-1)}
                aria-label="Önceki ürünler"
                className="absolute left-1.5 top-[calc(50%-1rem)] hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/95 text-gray-800 shadow-lg backdrop-blur transition hover:scale-105 hover:bg-white sm:flex"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {canNext && (
              <button
                type="button"
                onClick={() => scrollByCards(1)}
                aria-label="Sonraki ürünler"
                className="absolute right-1.5 top-[calc(50%-1rem)] hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/95 text-gray-800 shadow-lg backdrop-blur transition hover:scale-105 hover:bg-white sm:flex"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
